import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { FormControl, TextField, CircularProgress } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  Button,
  Select,
  BreadcrumbsBlock,
  ProductSelectableOption,
} from 'components';
import {
  NavigateBefore,
  NavigateNext,
  ShoppingCart,
} from '@mui/icons-material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Thumbs, FreeMode } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/free-mode';
import '../../ProductItem.styled.scss';
import { PRODUCT_TYPE, TProductCustomField } from 'api/types';
import { TPhoneCaseProduct } from './PhoneCaseProduct.types';
import { addItemToLocalCart } from 'store/localCart';
import { useAppDispatch, useAppSelector } from 'store/store';
import { getPhoneCaseByIdAsyncThunk } from 'store/phoneCases/phoneCases.action';
import { resetPhoneCaseById } from 'store/phoneCases/phoneCases.slices';
import { resetStockList } from 'store/stock/stock.slices';
import { buildPhoneCasesAvailability } from '../../ProductPage.types';
import { CustomRadioGroup } from './components/CustomRadioGroup/CustomRadioGroup';
import { TStockTypeEnum } from 'api/stock/stock.types';
import { getStockListByTypeAsyncThunk } from 'store/stock/stock.action';
import { hasMissingVariation } from 'components/templates/Cart/Cart.utils';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';

export const PhoneCaseProduct: TPhoneCaseProduct = () => {
  const localize = useLocalize();
  const { productId } = useParams<{ productId: string }>();
  const dispatch = useAppDispatch();

  const {
    phoneCases: {
      phoneCaseById: {
        data: product,
        isLoading: isPhoneCaseLoading,
        error: phoneCaseError,
      },
    },
    stock: {
      stockListByType: { data: stockListByType },
    },
  } = useAppSelector((store) => store);

  const isLoading = isPhoneCaseLoading;

  const phoneCasesAvailability = useMemo(
    () =>
      buildPhoneCasesAvailability(stockListByType?.[TStockTypeEnum.PhoneCase]),
    [stockListByType]
  );

  React.useEffect(() => {
    if (productId) {
      dispatch(getPhoneCaseByIdAsyncThunk({ id: productId }));
    }

    return () => {
      dispatch(resetPhoneCaseById());
    };
  }, [productId, dispatch]);

  useEffect(() => {
    dispatch(
      getStockListByTypeAsyncThunk({
        queryParams: { typeList: [TStockTypeEnum.PhoneCase] },
      })
    );
    return () => {
      dispatch(resetStockList());
    };
  }, [dispatch]);

  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [selectedPhoneModel, setSelectedPhoneModel] = useState<string>('');
  const [selectedVariation, setSelectedVariation] = useState<string>('');
  const [customFieldValues, setCustomFieldValues] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const list = phoneCasesAvailability.phoneModelsList;
    if (!list.length) return;
    if (!selectedPhoneModel || !list.includes(selectedPhoneModel)) {
      setSelectedPhoneModel(list[0]);
      setSelectedVariation('');
    }
  }, [phoneCasesAvailability.phoneModelsList, selectedPhoneModel]);

  const handleCustomFieldChange = (fieldName: string, value: string) => {
    setCustomFieldValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  if (isLoading) {
    return (
      <div className="product-page__loading">
        <CircularProgress />
      </div>
    );
  }

  if (phoneCaseError || !product) {
    return (
      <h2 className="product-page__error">
        {localize('product.phone.case.not.found')}
      </h2>
    );
  }

  const images = product.images || [];
  const imageUrls = images
    .map((img) => (typeof img === 'string' ? img : URL.createObjectURL(img)))
    .filter(Boolean);

  const handleAddToCart = () => {
    if (!selectedPhoneModel || !selectedVariation) {
      return;
    }
    dispatch(
      addItemToLocalCart({
        id: Math.random().toString(36).substring(2, 15),
        productId: product.id,
        productName: product.name,
        productType: PRODUCT_TYPE.PhoneCase,
        price: product.price,
        productImage: product.images[0],
        quantity: 1,
        customOptions: customFieldValues,
        stock: [
          {
            stockItem: stockListByType?.phoneCase.find(
              (stock) =>
                stock.name === selectedPhoneModel &&
                stock.variation === selectedVariation
            ),
            quantity: 1,
          },
        ],
      })
    );
  };

  return (
    <>
      <BreadcrumbsBlock idHelpers={[{ id: product.id, title: product.name }]} />
      <h2 className="product-page__title">{localize(product.name)}</h2>
      <div className="product-item">
        <div className="product-item__content-wrapper">
          <div className="product-item__media">
            {imageUrls.length > 0 ? (
              <>
                <div className="product-item__image-container">
                  <Swiper
                    spaceBetween={10}
                    navigation={{
                      nextEl: '.swiper-button-next-custom',
                      prevEl: '.swiper-button-prev-custom',
                    }}
                    thumbs={{
                      swiper:
                        thumbsSwiper && !thumbsSwiper.destroyed
                          ? thumbsSwiper
                          : null,
                    }}
                    modules={[Navigation, Thumbs]}
                    className="product-item__main-swiper"
                  >
                    {imageUrls.map((url, index) => (
                      <SwiperSlide key={index}>
                        <img
                          src={url}
                          alt={`${product.name} - ${index + 1}`}
                          className="product-item__image"
                        />
                      </SwiperSlide>
                    ))}
                    <div className="swiper-button-prev-custom">
                      <NavigateBefore />
                    </div>
                    <div className="swiper-button-next-custom">
                      <NavigateNext />
                    </div>
                  </Swiper>
                  <div className="product-item__features">
                    <div className="product-item__feature">
                      <span className="product-item__feature-icon">🧲</span>
                      <span>
                        {localize('product.phone.case.feature.magnetic')}
                      </span>
                    </div>
                    <div className="product-item__feature">
                      <span className="product-item__feature-icon">📱</span>
                      <span>
                        {localize('product.phone.case.feature.magsafe')}
                      </span>
                    </div>
                    <div className="product-item__feature">
                      <span className="product-item__feature-icon">🎯</span>
                      <span>
                        {localize('product.phone.case.feature.alignment')}
                      </span>
                    </div>
                  </div>
                </div>
                {imageUrls.length > 1 && (
                  <Swiper
                    onSwiper={setThumbsSwiper}
                    spaceBetween={10}
                    slidesPerView={4}
                    freeMode={true}
                    watchSlidesProgress={true}
                    modules={[FreeMode, Thumbs]}
                    className="product-item__thumbs-swiper"
                  >
                    {imageUrls.map((url, index) => (
                      <SwiperSlide key={index}>
                        <img
                          src={url}
                          alt={`${product.name} thumbnail - ${index + 1}`}
                          className="product-item__thumb-image"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                )}
              </>
            ) : (
              <div className="product-item__image-container product-item__image-container--empty">
                <p>{localize('product.phone.case.no.images')}</p>
              </div>
            )}
          </div>

          <div className="product-item__info">
            <p className="product-item__code">
              {localize('product.phone.case.code.prefix', [String(product.id)])}
            </p>
            <span className="product-item__availability-badge">
              {localize('product.phone.case.in.stock')}
            </span>
            <div className="product-item__price">
              {product.newPrice ? (
                <>
                  <span className="product-item__price-old">
                    {localize('product.phone.case.price.zl', [
                      String(product.price),
                    ])}
                  </span>{' '}
                  <span className="product-item__price-new">
                    {localize('product.phone.case.price.zl', [
                      String(product.newPrice),
                    ])}
                  </span>
                </>
              ) : (
                <span>
                  {localize('product.phone.case.price.zl', [
                    String(product.price),
                  ])}
                </span>
              )}
            </div>
            <div className="product-item__options-section">
              <h5 className="product-item__options-section-title">
                {localize('product.phone.case.options.phone.model')}
              </h5>
              <div>
                <Select
                  id="poc-phone-model"
                  label={
                    localize(
                      'product.phone.case.options.phone.model.poc'
                    ) as string
                  }
                  options={phoneCasesAvailability.phoneModelsList.map(
                    (model) => ({
                      id: model,
                      label: model,
                    })
                  )}
                  value={selectedPhoneModel}
                  onChange={(e: SelectChangeEvent<string | string[]>) => {
                    const v = e.target.value;
                    const next = typeof v === 'string' ? v : (v[0] ?? '');
                    setSelectedPhoneModel(next);
                    setSelectedVariation('');
                  }}
                />
              </div>
              <div className="product-item__options-section-radio-group">
                {phoneCasesAvailability.variationsList?.map?.(
                  (variationName) => (
                    <ProductSelectableOption
                      key={variationName}
                      label={variationName}
                      isSelected={selectedVariation === variationName}
                      onChange={() => setSelectedVariation(variationName)}
                      disabled={
                        !selectedPhoneModel ||
                        hasMissingVariation(
                          phoneCasesAvailability,
                          selectedPhoneModel,
                          variationName
                        )
                      }
                    />
                  )
                )}
              </div>
            </div>

            {product.customFields && product.customFields.length > 0 && (
              <div className="product-item__custom-fields">
                <h5 className="product-item__custom-fields-title">
                  {localize('product.phone.case.custom.options.title')}
                </h5>
                {product.customFields.map(
                  (field: TProductCustomField, index: number) => (
                    <FormControl
                      fullWidth
                      key={index}
                      className="product-item__custom-field"
                    >
                      {field.type === 'string' || field.type === 'text' ? (
                        <TextField
                          label={localize(field.name) as string}
                          placeholder={localize(field.placeholder) as string}
                          value={customFieldValues[field.name] || ''}
                          onChange={(e) =>
                            handleCustomFieldChange(field.name, e.target.value)
                          }
                          inputProps={{
                            maxLength: field.maxLength,
                            minLength: field.minLength,
                          }}
                          required
                          fullWidth
                        />
                      ) : field.type === 'radio' || field.type === 'Radio' ? (
                        <CustomRadioGroup
                          productCustomField={field}
                          value={customFieldValues[field.name] || ''}
                          onChange={(value) =>
                            handleCustomFieldChange(field.name, value)
                          }
                        />
                      ) : null}
                    </FormControl>
                  )
                )}
              </div>
            )}

            {product.description && (
              <div className="product-item__description-inline">
                <h5>{localize('product.phone.case.description.title')}</h5>
                <p>{localize(product.description)}</p>
              </div>
            )}

            <div className="product-item__button-group">
              <Button
                variant="contained"
                fullWidth
                className="product-item__add-to-cart"
                startIcon={<ShoppingCart />}
                size="large"
                onClick={handleAddToCart}
                disabled={!selectedPhoneModel || !selectedVariation}
              >
                {localize('product.phone.case.add.to.cart')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
